import {AuxFunctions} from './AuxFunctions';

export class TweenListObj {

    public scope: unknown;	// Object affected by this tweening
    public properties: any;	    // List of properties that are tweened (PropertyInfoObj instances)
    // .valueStart					:number		// Initial value of the property
    // .valueComplete				:number		// The value the property should have when completed
    public timeStart: number;	// Time when this tweening should start
    public timeComplete: number;	// Time when this tweening should end
    public useFrames: boolean;	// Whether or not to use frames instead of time
    public transition: Function;	// Equation to control the transition animation
    public transitionParams: Object;	// Additional parameters for the transition
    public onStart: Function;	// Function to be executed on the object when the tween starts (once)
    public onUpdate: Function;	// Function to be executed on the object when the tween updates (several times)
    public onComplete: Function;	// Function to be executed on the object when the tween completes (once)
    public onOverwrite: Function;	// Function to be executed on the object when the tween is overwritten
    public onError: Function;	// Function to be executed if an error is thrown when tweener exectues a callback (onComplete, onUpdate etc)
    public onStartParams: Array<any>;		// Array of parameters to be passed for the event
    public onUpdateParams: Array<any>;		// Array of parameters to be passed for the event
    public onCompleteParams: Array<any>;		// Array of parameters to be passed for the event
    public onOverwriteParams: Array<any>;		// Array of parameters to be passed for the event
    public onStartScope: Object;	// Scope in which the event function is ran
    public onUpdateScope: Object;	// Scope in which the event function is ran
    public onCompleteScope: Object;	// Scope in which the event function is ran
    public onOverwriteScope: Object;	// Scope in which the event function is ran
    public onErrorScope: Object;	// Scope in which the event function is ran
    public rounded: boolean;	// Use rounded values when updating
    public isPaused: boolean;	// Whether or not this tween is paused
    public timePaused: number;	// Time when this tween was paused
    public isCaller: boolean;	// Whether or not this tween is a "caller" tween
    public count: number;	// number of times this caller should be called
    public timesCalled: number;	// How many times the caller has already been called ("caller" tweens only)
    public waitFrames: boolean;	// Whether or not this caller should wait at least one frame for each call execution ("caller" tweens only)
    public skipUpdates: number;	// How many updates should be skipped (default = 0; 1 = update-skip-update-skip...)
    public updatesSkipped: number;	// How many updates have already been skipped
    public hasStarted: boolean;	// Whether or not this tween has already started

    // ==================================================================================================================================
    // CONSTRUCTOR function -------------------------------------------------------------------------------------------------------------

    /**
     * Initializes the basic TweenListObj.
     *
     * @param p_scope Object affected by this tweening
     * @param p_timeStart Time when this tweening should start
     * @param p_timeComplete Time when this tweening should end
     * @param p_useFrames Whether or not to use frames instead of time
     * @param p_transition Equation to control the transition animation
     * @param p_transitionParams
     */
    constructor(p_scope: any, p_timeStart: number, p_timeComplete: number, p_useFrames: boolean, p_transition: Function, p_transitionParams: any) {
        this.scope = p_scope;
        this.timeStart = p_timeStart;
        this.timeComplete = p_timeComplete;
        this.useFrames = p_useFrames;
        this.transition = p_transition;
        this.transitionParams = p_transitionParams;

        // Other default information
        this.properties = {};
        this.isPaused = false;
        this.timePaused = undefined;
        this.isCaller = false;
        this.updatesSkipped = 0;
        this.timesCalled = 0;
        this.skipUpdates = 0;
        this.hasStarted = false;
    }


    // ==================================================================================================================================
    // OTHER functions ------------------------------------------------------------------------------------------------------------------

    /**
     * Clones this tweening and returns the new TweenListObj
     *
     * @param omitEvents Whether or not events such as onStart (and its parameters) should be omitted
     *
     * @return A copy of this object
     */
    public clone(omitEvents: boolean): TweenListObj {
        const nTween: TweenListObj = new TweenListObj(this.scope, this.timeStart, this.timeComplete, this.useFrames, this.transition, this.transitionParams);
        nTween.properties = [];
        for (const pName in this.properties) {
            nTween.properties[pName] = this.properties[pName].clone();
        }
        nTween.skipUpdates = this.skipUpdates;
        nTween.updatesSkipped = this.updatesSkipped;
        if (!omitEvents) {
            nTween.onStart = this.onStart;
            nTween.onUpdate = this.onUpdate;
            nTween.onComplete = this.onComplete;
            nTween.onOverwrite = this.onOverwrite;
            nTween.onError = this.onError;
            nTween.onStartParams = this.onStartParams;
            nTween.onUpdateParams = this.onUpdateParams;
            nTween.onCompleteParams = this.onCompleteParams;
            nTween.onOverwriteParams = this.onOverwriteParams;
            nTween.onStartScope = this.onStartScope;
            nTween.onUpdateScope = this.onUpdateScope;
            nTween.onCompleteScope = this.onCompleteScope;
            nTween.onOverwriteScope = this.onOverwriteScope;
            nTween.onErrorScope = this.onErrorScope;
        }
        nTween.rounded = this.rounded;
        nTween.isPaused = this.isPaused;
        nTween.timePaused = this.timePaused;
        nTween.isCaller = this.isCaller;
        nTween.count = this.count;
        nTween.timesCalled = this.timesCalled;
        nTween.waitFrames = this.waitFrames;
        nTween.hasStarted = this.hasStarted;

        return nTween;
    }

    /**
     * Returns this object described as a string.
     *
     * @return The description of this object.
     */
    public toString(): string {
        let returnStr: string = '\n[TweenListObj ';
        returnStr += 'scope:' + this.scope;
        returnStr += ', properties:';
        let isFirst: boolean = true;
        for (const i in this.properties) {
            if (!isFirst) returnStr += ',';
            returnStr += '[name:' + this.properties[i].name;
            returnStr += ',valueStart:' + this.properties[i].valueStart;
            returnStr += ',valueComplete:' + this.properties[i].valueComplete;
            returnStr += ']';
            isFirst = false;
        }
        returnStr += ', timeStart:' + this.timeStart;
        returnStr += ', timeComplete:' + this.timeComplete;
        returnStr += ', useFrames:' + this.useFrames;
        returnStr += ', transition:' + this.transition;
        returnStr += ', transitionParams:' + this.transitionParams;

        if (this.skipUpdates) returnStr += ', skipUpdates:' + this.skipUpdates;
        if (this.updatesSkipped) returnStr += ', updatesSkipped:' + this.updatesSkipped;

        if (this.onStart !== null) returnStr += ', onStart:' + this.onStart;
        if (this.onUpdate !== null) returnStr += ', onUpdate:' + this.onUpdate;
        if (this.onComplete !== null) returnStr += ', onComplete:' + this.onComplete;
        if (this.onOverwrite !== null) returnStr += ', onOverwrite:' + this.onOverwrite;
        if (this.onError !== null) returnStr += ', onError:' + this.onError;

        if (this.onStartParams) returnStr += ', onStartParams:' + this.onStartParams;
        if (this.onUpdateParams) returnStr += ', onUpdateParams:' + this.onUpdateParams;
        if (this.onCompleteParams) returnStr += ', onCompleteParams:' + this.onCompleteParams;
        if (this.onOverwriteParams) returnStr += ', onOverwriteParams:' + this.onOverwriteParams;

        if (this.onStartScope) returnStr += ', onStartScope:' + this.onStartScope;
        if (this.onUpdateScope) returnStr += ', onUpdateScope:' + this.onUpdateScope;
        if (this.onCompleteScope) returnStr += ', onCompleteScope:' + this.onCompleteScope;
        if (this.onOverwriteScope) returnStr += ', onOverwriteScope:' + this.onOverwriteScope;
        if (this.onErrorScope) returnStr += ', onErrorScope:' + this.onErrorScope;

        if (this.rounded) returnStr += ', rounded:' + this.rounded;
        if (this.isPaused) returnStr += ', isPaused:' + this.isPaused;
        if (this.timePaused) returnStr += ', timePaused:' + this.timePaused;
        if (this.isCaller) returnStr += ', isCaller:' + this.isCaller;
        if (this.count) returnStr += ', count:' + this.count;
        if (this.timesCalled) returnStr += ', timesCalled:' + this.timesCalled;
        if (this.waitFrames) returnStr += ', waitFrames:' + this.waitFrames;
        if (this.hasStarted) returnStr += ', hasStarted:' + this.hasStarted;

        returnStr += ']\n';
        return returnStr;
    }

    /**
     * Checks if p_obj "inherits" properties from other objects, as set by the "base" property. Will create a new object, leaving others intact.
     * o_bj.base can be an object or an array of objects. Properties are collected from the first to the last element of the "base" filed, with higher
     * indexes overwriting smaller ones. Does not modify any of the passed objects, but makes a shallow copy of all properties.
     *
     * @param p_obj Object that should be tweened: a movieclip, textfield, etc. OR an array of objects
     *
     * @return A new object with all properties from the p_obj and p_obj.base.
     */

    public static makePropertiesChain(p_obj: any): any {
        // Is this object inheriting properties from another object?
        const baseObject: any = p_obj.base;
        if (baseObject) {
            // object inherits. Are we inheriting from an object or an array
            let chainedObject: any = {};
            let chain: Array<any>;
            if (baseObject instanceof Array) {
                // Inheritance chain is the base array
                chain = [];
                // make a shallow copy
                for (let k: number = 0; k < baseObject.length; k++) chain.push(baseObject[k]);
            } else {
                // Only one object to be added to the array
                chain = [baseObject];
            }
            // add the final object to the array, so it's properties are added last
            chain.push(p_obj);
            let currChainObj: Object;
            // Loops through each object adding it's property to the final object
            const len: number = chain.length;
            for (let i: number = 0; i < len; i++) {
                if (chain[i]['base']) {
                    // deal with recursion: watch the order! "parent" base must be concatenated first!
                    currChainObj = AuxFunctions.concatObjects(this.makePropertiesChain(chain[i]['base']), chain[i]);
                } else {
                    currChainObj = chain[i];
                }
                chainedObject = AuxFunctions.concatObjects(chainedObject, currChainObj);
            }
            if (chainedObject['base']) {
                delete chainedObject['base'];
            }
            return chainedObject;
        } else {
            // No inheritance, just return the object itself
            return p_obj;
        }
    }
}
